import { useCallback, useEffect, useState } from "react";
import { useUserState } from "../state/user";
import {
  ClinicalRegistry,
  RegistryPrivacy,
  RegistryType,
  createClinicalRegistry,
  deleteClinicalRegistry,
  extractShortText,
  getPatientClinicalRegistry,
  listPatientClinicalRegistries,
  updateClinicalRegistry
} from "../services/clinicalRegistries";

export type Privacy = "only-me" | "colleagues" | "health-professionals";

export interface ClinicalRecord {
  id: string;
  patientId: string;
  // Author identity is display-only and injected by the consumer. Clinical files
  // aren't shared yet, so every registry returned for a patient was authored by
  // the current professional; when sharing lands, the author will have to be
  // resolved from `createdBy` via a dedicated endpoint.
  doctorName: string;
  doctorSpecialty: string;
  doctorPictureUrl?: string;
  // Canonical note/summary discriminator — RegistryType, not a localized label.
  type: RegistryType;
  content: string;
  privacy: Privacy;
  visibleToPatient: boolean;
  createdAt: string;
  updatedAt: string;
}

interface Config {
  nhsId: string;
  clinicalFileId?: string;
}

// ── Enum <-> UI mapping ───────────────────────────────────────────────────────

const privacyToInt: Record<Privacy, number> = {
  "only-me": RegistryPrivacy.ONLY_ME,
  colleagues: RegistryPrivacy.CLASSIFIED,
  "health-professionals": RegistryPrivacy.ALL
};

function intToPrivacy(value: number): Privacy {
  switch (value) {
    case RegistryPrivacy.CLASSIFIED:
      return "colleagues";
    case RegistryPrivacy.ALL:
      return "health-professionals";
    default:
      return "only-me";
  }
}

function generateId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  // Fallback for older environments
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, char => {
    const random = (Math.random() * 16) | 0;
    const value = char === "x" ? random : (random & 0x3) | 0x8;
    return value.toString(16);
  });
}

function toRecord(reg: ClinicalRegistry, nhsId: string): ClinicalRecord {
  return {
    id: reg.id,
    patientId: nhsId,
    doctorName: "",
    doctorSpecialty: "",
    doctorPictureUrl: undefined,
    type: reg.type,
    content: reg.formattedText ?? "",
    privacy: intToPrivacy(reg.privacy),
    visibleToPatient: reg.viewableByPatient,
    createdAt: reg.createdAt,
    updatedAt: reg.updatedAt ?? reg.createdAt
  };
}

function byDate(a: ClinicalRecord, b: ClinicalRecord) {
  return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime();
}

export function useClinicalRecords({ nhsId, clinicalFileId }: Config) {
  const { token } = useUserState();
  const [records, setRecords] = useState<ClinicalRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<unknown>(null);

  const refresh = useCallback(async () => {
    if (!token || !nhsId) return;
    setLoading(true);
    try {
      const registries = await listPatientClinicalRegistries(token, nhsId);
      setRecords(
        registries.map(registry => toRecord(registry, nhsId)).sort(byDate)
      );
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [token, nhsId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Single GET for one entry — used when opening a record so its full, fresh
  // content is loaded from the server rather than reused from the list.
  const fetchRecord = useCallback(
    async (id: string): Promise<ClinicalRecord | null> => {
      if (!token || !nhsId) return null;
      const reg = await getPatientClinicalRegistry(token, nhsId, id);
      return toRecord(reg, nhsId);
    },
    [token, nhsId]
  );

  const addRecord = useCallback(
    async (data: {
      type: RegistryType;
      content: string;
      privacy: Privacy;
      visibleToPatient: boolean;
    }): Promise<ClinicalRecord | null> => {
      if (!token || !clinicalFileId) return null;
      const reg = await createClinicalRegistry(token, {
        id: generateId(),
        clinicalFileId,
        type: data.type,
        privacy: privacyToInt[data.privacy],
        viewableByPatient: data.visibleToPatient,
        createdAt: new Date().toISOString(),
        shortText: extractShortText(data.content),
        formattedText: data.content
      });
      const record = toRecord(reg, nhsId);
      setRecords(prev => [record, ...prev].sort(byDate));
      return record;
    },
    [token, nhsId, clinicalFileId]
  );

  const updateRecord = useCallback(
    async (
      id: string,
      data: {
        content: string;
        privacy: Privacy;
        visibleToPatient: boolean;
      }
    ): Promise<void> => {
      if (!token) return;
      const reg = await updateClinicalRegistry(token, id, {
        privacy: privacyToInt[data.privacy],
        viewableByPatient: data.visibleToPatient,
        shortText: extractShortText(data.content),
        formattedText: data.content
      });
      const updated = toRecord(reg, nhsId);
      setRecords(prev =>
        prev.map(record => (record.id === id ? updated : record)).sort(byDate)
      );
    },
    [token, nhsId]
  );

  const deleteRecord = useCallback(
    async (id: string): Promise<void> => {
      if (!token) return;
      await deleteClinicalRegistry(token, id);
      setRecords(prev => prev.filter(record => record.id !== id));
    },
    [token]
  );

  const notes = records.filter(record => record.type === RegistryType.NOTE);
  const summaries = records.filter(
    record => record.type === RegistryType.SUMMARY
  );

  return {
    records,
    notes,
    summaries,
    loading,
    error,
    refresh,
    fetchRecord,
    addRecord,
    updateRecord,
    deleteRecord
  };
}
